import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import type {
  LearningDevelopmentCatalogSlug,
  LearningDevelopmentOption,
} from '../../learning-development/learningDevelopmentTypes';

/*
  Plausible course titles, which is the right call here for the reason `sampleEducation`'s school
  names are: a course title never leaves `rsys_` and never lands in the identity store, and an
  unreadable one would make the generated profile worse at the one job it has. The rule is about
  where a value LANDS, not which generator it came from.
*/
const TITLES = [
  'Basic Government Procurement',
  'Records and Archives Management',
  'Data Privacy Act Orientation',
  'Supervisory Development Course',
  'Occupational Safety and Health Basics',
];

/** The four vocabularies, as the modal has already loaded them. */
export type TrainingVocabularies = Record<
  LearningDevelopmentCatalogSlug,
  readonly LearningDevelopmentOption[]
>;

export interface SampleTraining {
  title: string;

  /** Null only where the vocabulary is empty — there is no id to invent. */
  typeId: string | null;
  categoryId: string | null;
  interventionId: string | null;
  sponsorId: string | null;

  dateFrom: string;
  dateTo: string;
  numberOfHours: string;
}

/**
 * Test data for the Training/L&D form's Autogenerate.
 *
 * **Every vocabulary value is a REAL row the modal already loaded**, which is what the
 * foreign-key rule asks for: an id this generator invented would fail the server's "it must be a
 * live row" check, and that failure reads as a bug in the form rather than in the generator.
 *
 * **An empty vocabulary yields null rather than a guess**, and the caller leaves the picker
 * alone. That is the honest outcome for the SPONSOR list in particular, which ships empty and
 * fills from real use — a generator that created a sponsor row would be writing to a catalog
 * behind the reader's back, which is a different permission and a different decision.
 *
 * **It fills the optional fields too.** The floor is that the primary action enables; the point
 * is that the screens behind the form get exercised, and a record carrying only its required
 * fields produces a panel of em dashes that exercises nothing.
 *
 * **Nothing here touches the competency-development block**, which the form no longer asks for.
 * A generator that filled a field the reader cannot see would be writing a value nobody chose.
 *
 * No record number: it is assigned by the server and the request carries no field for it.
 */
export function sampleTraining(vocabularies: TrainingVocabularies): SampleTraining {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    Calendar dates built from parts, never `toISOString` — that converts through UTC and can hand
    the field the day before the one intended. A short course a few years back, which satisfies
    every date rule on this form: not in the future, and ending after it began.
  */
  const today = todayCalendarDate();
  const [year, month, day] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  const courseYear = year - 1 - Math.floor(Math.random() * 4);

  const pick = (catalog: LearningDevelopmentCatalogSlug): string | null => {
    const options = vocabularies[catalog];
    return options.length ? options[Math.floor(Math.random() * options.length)].id : null;
  };

  return {
    title: `${TITLES[Math.floor(Math.random() * TITLES.length)]} ${tag}`,

    typeId: pick('types'),
    categoryId: pick('categories'),
    interventionId: pick('interventions'),
    sponsorId: pick('sponsors'),

    dateFrom: `${courseYear}-${pad(month)}-${pad(day)}`,
    dateTo: `${courseYear}-${pad(month)}-${pad(day)}`,
    numberOfHours: String(8 * (1 + Math.floor(Math.random() * 5))),
  };
}
